import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format, parseISO, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Eye, Users, MousePointerClick, Calculator, TrendingUp, TrendingDown, Minus, RefreshCw,
  CalendarRange, FileText, BookOpen, Phone, MessageCircle, Mail, ClipboardList, CalendarCheck, Briefcase, Ticket,
  Smartphone, Monitor, Tablet, Globe,
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { adminGet, reportError, fadeUp } from './adminApi';

const PRESETS = [
  { id: '7', label: '7 jours', days: 7 },
  { id: '30', label: '30 jours', days: 30 },
  { id: '90', label: '90 jours', days: 90 },
];

const TRIGGER_ICONS = { devis: ClipboardList, rdv: CalendarCheck, contact: Ticket, candidature: Briefcase, phone: Phone, whatsapp: MessageCircle, email: Mail };
const DEVICE_ICONS = { mobile: Smartphone, desktop: Monitor, tablet: Tablet };

const isoDay = (d) => format(d, 'yyyy-MM-dd');
const fmtDay = (iso, pattern = 'd MMM') => format(parseISO(iso), pattern, { locale: fr });
const fmtNum = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);
const pct = (part, total) => (total ? Math.round((part / total) * 100) : 0);

/** Resolve theme tokens for SVG charts (CSS variables are not resolved inside SVG attributes). */
function useChartColors() {
  return useMemo(() => {
    const read = (name, fallback) => {
      if (typeof window === 'undefined') return fallback;
      const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      return v ? `hsl(${v})` : fallback;
    };
    return { primary: read('--primary', 'hsl(182 60% 32%)'), accent: read('--accent', 'hsl(182 75% 42%)'), muted: read('--muted-foreground', 'hsl(215 16% 47%)') };
  }, []);
}

function Delta({ value }) {
  if (value === null || value === undefined) return <span className="text-[11px] text-muted-foreground inline-flex items-center gap-0.5"><Minus className="h-3 w-3" /> nouveau</span>;
  const up = value > 0; const flat = value === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const cls = flat ? 'text-muted-foreground' : up ? 'text-[hsl(var(--primary))]' : 'text-destructive';
  return <span className={`text-[11px] inline-flex items-center gap-0.5 ${cls}`}><Icon className="h-3 w-3" /> {up ? '+' : ''}{value.toLocaleString('fr-FR')} %</span>;
}

function KpiTile({ icon: Icon, label, value, delta, hint, testId, highlight = false, suffix = '', showDelta = true }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--brand-mint))]/30' : 'border-border bg-card'}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-[hsl(var(--primary))]" />
      </div>
      <p className="text-2xl font-['Space_Grotesk'] font-semibold" data-testid={`${testId}-value`}>{fmtNum(value)}{suffix}</p>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        {showDelta && <Delta value={delta} />}
        {hint && <span className="text-[11px] text-muted-foreground truncate">{hint}</span>}
      </div>
    </div>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children, testId, action }) {
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center shrink-0"><Icon className="h-[18px] w-[18px] text-[hsl(var(--primary))]" /></div>
          <div>
            <h3 className="font-['Space_Grotesk'] font-semibold leading-tight">{title}</h3>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}

function BarRow({ icon: Icon, label, count, total, testId, sub }) {
  const width = pct(count, total);
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
          <span className="truncate">{label}</span>
          {sub && <span className="text-[11px] text-muted-foreground truncate">{sub}</span>}
        </span>
        <span className="font-medium tabular-nums shrink-0">{fmtNum(count)} <span className="text-[11px] text-muted-foreground font-normal">({width} %)</span></span>
      </div>
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full bg-[hsl(var(--primary))] transition-[width] duration-300" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function TopTable({ rows, emptyText, testId, kind }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground py-6 text-center" data-testid={`${testId}-empty`}>{emptyText}</p>;
  const max = rows[0]?.views || 1;
  return (
    <div className="rounded-xl border border-border overflow-x-auto">
      <Table data-testid={testId}>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs w-8">#</TableHead>
            <TableHead className="text-xs">{kind === 'articles' ? 'Article' : 'Page'}</TableHead>
            <TableHead className="text-xs text-right">Vues</TableHead>
            <TableHead className="text-xs text-right hidden sm:table-cell">Visites</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.path} data-testid={`${testId}-row`}>
              <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
              <TableCell className="max-w-[320px]">
                <a href={r.path} target="_blank" rel="noopener noreferrer" className="block text-sm font-medium truncate hover:text-[hsl(var(--primary))] hover:underline">{r.title || r.path}</a>
                <span className="block text-[11px] text-muted-foreground truncate">{r.path}</span>
                <div className="mt-1 h-1 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${pct(r.views, max)}%` }} /></div>
              </TableCell>
              <TableCell className="text-sm text-right font-medium tabular-nums">{fmtNum(r.views)}</TableCell>
              <TableCell className="text-sm text-right tabular-nums hidden sm:table-cell">{fmtNum(r.visits)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card/95 backdrop-blur-md px-3 py-2 shadow-md text-xs">
      <p className="font-medium mb-1 capitalize">{fmtDay(label, 'EEEE d MMMM')}</p>
      {payload.map((p) => <p key={p.dataKey} className="flex justify-between gap-4"><span className="text-muted-foreground">{p.name}</span><span className="font-medium tabular-nums">{fmtNum(p.value)}</span></p>)}
    </div>
  );
}

function AnalyticsStateGuard({ loading, error, data, onRetry }) {
  if (data) return null;
  if (loading) {
    return (
      <div className="space-y-5" data-testid="analytics-loading">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={`kpi-skel-${i}`} className="h-28 rounded-xl" />)}</div>
        <Skeleton className="h-72 rounded-xl" />
        <div className="grid lg:grid-cols-2 gap-5"><Skeleton className="h-64 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>
      </div>
    );
  }
  if (error) {
    return (
      <Card className="p-8 text-center" data-testid="analytics-error">
        <p className="text-sm text-muted-foreground mb-3">Les statistiques n'ont pas pu être chargées.</p>
        <Button size="sm" variant="outline" onClick={onRetry}>Réessayer</Button>
      </Card>
    );
  }
  return null;
}

/**
 * Admin tab: internal audience dashboard (visits, top pages/articles, simulator usage, conversion triggers).
 */
export function AdminAnalyticsTab({ adminKey }) {
  const [preset, setPreset] = useState('30');
  const [range, setRange] = useState(() => ({ from: subDays(new Date(), 29), to: new Date() }));
  const [draft, setDraft] = useState(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const colors = useChartColors();

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const res = await adminGet('/api/admin/analytics', adminKey, { from: isoDay(range.from), to: isoDay(range.to) });
      setData(res.data);
    } catch (err) {
      setError(true);
      reportError('statistiques de visites', err, () => toast.error('Impossible de charger les statistiques'));
    } finally {
      setLoading(false);
    }
  }, [adminKey, range]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (p) => {
    setPreset(p.id);
    setRange({ from: subDays(new Date(), p.days - 1), to: new Date() });
  };
  const applyCustom = () => {
    if (!draft?.from) return;
    setPreset('custom');
    setRange({ from: draft.from, to: draft.to || draft.from });
    setPickerOpen(false);
  };

  const k = data?.kpis;
  const rangeLabel = data ? `${fmtDay(data.range.from, 'd MMM yyyy')} → ${fmtDay(data.range.to, 'd MMM yyyy')} · ${data.range.days} j` : '';
  const chartData = (data?.daily || []).map((d) => ({ ...d, label: fmtDay(d.day) }));
  const tickEvery = Math.max(1, Math.ceil(chartData.length / 8));

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-5" data-testid="admin-analytics-tab">
      {/* Period selector */}
      <Card className="p-4 sm:p-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <h2 className="font-['Space_Grotesk'] font-semibold text-lg">Statistiques de visites</h2>
            <p className="text-xs text-muted-foreground" data-testid="analytics-range-label">{rangeLabel || 'Mesure interne, anonyme et sans cookie — les pages /admin sont exclues.'}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Période">
            {PRESETS.map((p) => (
              <Button key={p.id} size="sm" variant={preset === p.id ? 'default' : 'outline'} onClick={() => applyPreset(p)} data-testid={`analytics-preset-${p.id}`}>{p.label}</Button>
            ))}
            <Popover open={pickerOpen} onOpenChange={(o) => { setPickerOpen(o); if (o) setDraft(range); }}>
              <PopoverTrigger asChild>
                <Button size="sm" variant={preset === 'custom' ? 'default' : 'outline'} data-testid="analytics-preset-custom"><CalendarRange className="h-4 w-4 mr-1" /> Personnalisé</Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0" data-testid="analytics-date-picker">
                <Calendar mode="range" selected={draft} onSelect={setDraft} numberOfMonths={2} locale={fr} disabled={{ after: new Date() }} defaultMonth={subDays(new Date(), 30)} />
                <div className="flex items-center justify-between gap-2 border-t border-border p-3">
                  <span className="text-xs text-muted-foreground">{draft?.from ? `${format(draft.from, 'd MMM', { locale: fr })} → ${draft.to ? format(draft.to, 'd MMM yyyy', { locale: fr }) : '…'}` : 'Choisissez un début et une fin'}</span>
                  <Button size="sm" onClick={applyCustom} disabled={!draft?.from} data-testid="analytics-date-apply">Appliquer</Button>
                </div>
              </PopoverContent>
            </Popover>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} aria-label="Actualiser" data-testid="analytics-refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          </div>
        </div>
      </Card>

      <AnalyticsStateGuard loading={loading} error={error} data={data} onRetry={load} />
      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="analytics-kpis">
            <KpiTile icon={Users} label="Visites" value={k.visits} delta={k.deltas.visits} hint={`${fmtNum(k.visitors)} visiteurs`} testId="kpi-visits" />
            <KpiTile icon={Eye} label="Pages vues" value={k.page_views} delta={k.deltas.page_views} hint={k.visits ? `${(k.page_views / k.visits).toFixed(1)} / visite` : ''} testId="kpi-pageviews" />
            <KpiTile icon={Calculator} label="Simulations" value={k.simulator_uses} delta={k.deltas.simulator_uses} testId="kpi-simulator" />
            <KpiTile icon={MousePointerClick} label="Déclenchements" value={k.triggers} delta={k.deltas.triggers} hint="contact, devis, appel…" testId="kpi-triggers" />
            <KpiTile icon={TrendingUp} label="Taux de conversion" value={k.conversion_rate} suffix=" %" showDelta={false} hint="déclenchements / visites" testId="kpi-conversion" highlight />
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2" data-testid="analytics-compare-note">Évolution comparée aux {data.range.days} jours précédents.</p>

          {/* Daily chart */}
          <SectionCard icon={TrendingUp} title="Évolution des visites" subtitle="Visites et pages vues par jour" testId="analytics-chart-card">
            {k.page_views === 0 ? (
              <div className="h-56 flex flex-col items-center justify-center text-center" data-testid="analytics-chart-empty">
                <Eye className="h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">Aucune visite enregistrée sur cette période.</p>
                <p className="text-xs text-muted-foreground">Les statistiques se remplissent dès que des visiteurs parcourent le site.</p>
              </div>
            ) : (
              <div className="h-64" data-testid="analytics-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={colors.accent} stopOpacity={0.35} /><stop offset="100%" stopColor={colors.accent} stopOpacity={0} /></linearGradient>
                      <linearGradient id="gradVisits" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={colors.primary} stopOpacity={0.45} /><stop offset="100%" stopColor={colors.primary} stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-border" opacity={0.6} />
                    <XAxis dataKey="day" tickFormatter={(v) => fmtDay(v)} interval={tickEvery - 1} tick={{ fontSize: 11, fill: colors.muted }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: colors.muted }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: colors.muted, strokeDasharray: '3 3' }} />
                    <Area type="monotone" dataKey="page_views" name="Pages vues" stroke={colors.accent} strokeWidth={2} fill="url(#gradViews)" />
                    <Area type="monotone" dataKey="visits" name="Visites" stroke={colors.primary} strokeWidth={2} fill="url(#gradVisits)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </SectionCard>

          {/* Top pages / articles */}
          <div className="grid lg:grid-cols-2 gap-5">
            <SectionCard icon={FileText} title="Pages les plus vues" subtitle="Top 10 sur la période" testId="analytics-top-pages">
              <TopTable rows={data.top_pages} kind="pages" emptyText="Aucune page vue sur cette période." testId="top-pages-table" />
            </SectionCard>
            <SectionCard icon={BookOpen} title="Articles les plus lus" subtitle="Ptitconseils — astuces et tutoriels" testId="analytics-top-articles">
              <TopTable rows={data.top_articles} kind="articles" emptyText="Aucun article lu sur cette période." testId="top-articles-table" />
            </SectionCard>
          </div>

          {/* Simulator + triggers */}
          <div className="grid lg:grid-cols-2 gap-5">
            <SectionCard icon={Calculator} title="Utilisation des simulateurs" subtitle="Une utilisation = un visiteur qui ajuste le simulateur" testId="analytics-simulator"
              action={<Badge variant="secondary" className="text-xs" data-testid="simulator-total">{fmtNum(data.simulator.total)} simulation(s)</Badge>}>
              {data.simulator.total === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center" data-testid="simulator-empty">Aucune simulation sur cette période.</p>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-3">
                    {data.simulator.by_service.map((s) => <BarRow key={s.service} label={`Simulateur ${s.label}`} count={s.count} total={data.simulator.total} testId={`simulator-service-${s.service}`} />)}
                  </div>
                  {data.simulator.by_page.length > 0 && (
                    <div>
                      <p className="text-xs font-medium mb-2">Pages où le simulateur est le plus utilisé</p>
                      <div className="flex flex-wrap gap-1.5">
                        {data.simulator.by_page.map((p) => <Badge key={p.path} variant="outline" className="text-[11px] font-normal" data-testid="simulator-page-badge">{p.path} · {fmtNum(p.count)}</Badge>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </SectionCard>

            <SectionCard icon={MousePointerClick} title="Déclenchements" subtitle="Formulaires envoyés et clics de contact" testId="analytics-triggers"
              action={<Badge variant="secondary" className="text-xs" data-testid="triggers-total">{fmtNum(data.triggers.total)} au total</Badge>}>
              <div className="space-y-3">
                {data.triggers.items.map((t) => <BarRow key={t.name} icon={TRIGGER_ICONS[t.name]} label={t.label} count={t.count} total={data.triggers.total} testId={`trigger-${t.name}`} />)}
              </div>
            </SectionCard>
          </div>

          {/* Devices + sources */}
          <div className="grid lg:grid-cols-2 gap-5">
            <SectionCard icon={Smartphone} title="Appareils" subtitle="Répartition des pages vues" testId="analytics-devices">
              <div className="space-y-3">
                {data.devices.map((d) => <BarRow key={d.device} icon={DEVICE_ICONS[d.device]} label={d.label} count={d.count} total={k.page_views} testId={`device-${d.device}`} />)}
              </div>
            </SectionCard>
            <SectionCard icon={Globe} title="Sources de trafic" subtitle="D'où arrivent les visites (première page de chaque visite)" testId="analytics-sources">
              <div className="space-y-3">
                {data.sources.filter((s) => s.count > 0 || ['direct', 'google', 'facebook'].includes(s.source)).map((s) => (
                  <BarRow key={s.source} label={s.label} count={s.count} total={data.sources.reduce((a, b) => a + b.count, 0)} testId={`source-${s.source}`} />
                ))}
              </div>
              {data.referrers.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium mb-2">Sites référents</p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.referrers.map((r) => <Badge key={r.host} variant="outline" className="text-[11px] font-normal" data-testid="referrer-badge">{r.host} · {fmtNum(r.count)}</Badge>)}
                  </div>
                </div>
              )}
            </SectionCard>
          </div>
        </>
      )}
    </motion.div>
  );
}
